import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  confirmIndustrialCostEconomicMovementsImport,
  deleteIndustrialCostEconomicMovement,
  getIndustrialCostEconomicMovements,
  getIndustrialCostMovementClassifications,
  getIndustrialCostUploads,
  previewIndustrialCostEconomicMovementsImport,
  upsertIndustrialCostMovementClassification,
  type UpsertIndustrialCostMovementClassificationPayload,
} from '../services/industrialCostService';
import { analyzeFileNormalization, commitFileNormalization } from '../fileNormalization/service';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  ConfidenceLevel,
  CostType,
  EconomicMovement,
  EconomicType,
  MovementClassification,
  MovementClassificationBadgeStatus,
  MovementImportPreview,
  MovementImportSummary,
  MovementImportTypeMappingValue,
  MovementUploadMode,
  SourceGranularity,
  Upload,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES').format(date);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function formatAmount(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPeriod(year: number, month: number | null): string {
  if (!month) {
    return String(year);
  }

  return `${year}-${String(month).padStart(2, '0')}`;
}

function formatPercent(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}

function formatGranularityLabel(value: SourceGranularity | string | null | undefined): string {
  if (value === 'day') return 'Diaria';
  if (value === 'month') return 'Mensual';
  if (value === 'year') return 'Anual';
  return '—';
}

function formatDeclaredPeriod(
  year: number | null | undefined,
  month: number | null | undefined,
): string {
  if (!year) {
    return '—';
  }

  if (!month) {
    return String(year);
  }

  return `${year}-${String(month).padStart(2, '0')}`;
}

function resolveClassificationStatus(
  movementId: string,
  classificationsMap: Map<string, MovementClassification>,
): MovementClassificationBadgeStatus {
  const classification = classificationsMap.get(movementId);

  if (!classification) {
    return 'unclassified';
  }

  if (classification.classification_status === 'ignored') {
    return 'ignored';
  }

  if (classification.classification_status === 'classified') {
    return 'classified';
  }

  return 'unclassified';
}

function resolveEconomicClassificationLabel(
  movement: EconomicMovement,
  classificationsMap: Map<string, MovementClassification>,
): string {
  const classification = classificationsMap.get(movement.id);

  if (!classification) {
    return 'Pendiente';
  }

  if (classification.classification_status === 'ignored') {
    if (movement.source_type_code === 'VT') {
      return 'Venta';
    }

    if (movement.source_type_code === 'OI') {
      return 'Otros ingresos';
    }

    if (movement.source_type_code === 'NO') {
      return 'No imputable';
    }

    return 'Ignorado';
  }

  if (classification.economic_type === 'expense') {
    return 'Gasto';
  }

  if (classification.economic_type === 'cost' && classification.cost_type === 'direct') {
    return 'Coste directo';
  }

  if (classification.economic_type === 'cost' && classification.cost_type === 'indirect') {
    return 'Coste indirecto';
  }

  if (classification.classification_status === 'classified') {
    return 'Clasificado';
  }

  return 'Pendiente';
}

const ECONOMIC_CLASSIFICATION_CONFIG: Record<
  string,
  { color: string; bg: string; border: string }
> = {
  Pendiente: {
    color: '#cbd5e1',
    bg: 'rgba(148,163,184,0.10)',
    border: 'rgba(148,163,184,0.22)',
  },
  Ignorado: {
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.10)',
    border: 'rgba(245,158,11,0.24)',
  },
  Venta: {
    color: '#f97316',
    bg: 'rgba(249,115,22,0.10)',
    border: 'rgba(249,115,22,0.24)',
  },
  'Otros ingresos': {
    color: '#eab308',
    bg: 'rgba(234,179,8,0.10)',
    border: 'rgba(234,179,8,0.24)',
  },
  'No imputable': {
    color: '#94a3b8',
    bg: 'rgba(148,163,184,0.10)',
    border: 'rgba(148,163,184,0.22)',
  },
  Gasto: {
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.10)',
    border: 'rgba(96,165,250,0.24)',
  },
  'Coste directo': {
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.10)',
    border: 'rgba(34,197,94,0.24)',
  },
  'Coste indirecto': {
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.10)',
    border: 'rgba(167,139,250,0.24)',
  },
  Clasificado: {
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.10)',
    border: 'rgba(34,197,94,0.24)',
  },
};

const REQUIRED_FIELDS = ['CUENTA', 'DESCRIPCION', 'TIPO', 'SALDO'] as const;

const TYPE_MAPPING_OPTIONS: Array<{
  value: string;
  label: string;
  payload: MovementImportTypeMappingValue;
}> = [
  {
    value: 'unassigned',
    label: 'Sin asignar todavía',
    payload: {
      classification_status: null,
      economic_type: null,
      cost_type: null,
      source_type_label: null,
      is_auto_mapped: false,
    },
  },
  {
    value: 'cost_direct',
    label: 'Coste directo',
    payload: {
      classification_status: 'classified',
      economic_type: 'cost',
      cost_type: 'direct',
      source_type_label: 'COSTE DIRECTO',
      is_auto_mapped: false,
    },
  },
  {
    value: 'cost_indirect',
    label: 'Coste indirecto',
    payload: {
      classification_status: 'classified',
      economic_type: 'cost',
      cost_type: 'indirect',
      source_type_label: 'COSTE INDIRECTO',
      is_auto_mapped: false,
    },
  },
  {
    value: 'expense',
    label: 'Gasto',
    payload: {
      classification_status: 'classified',
      economic_type: 'expense',
      cost_type: null,
      source_type_label: null,
      is_auto_mapped: false,
    },
  },
  {
    value: 'ignored',
    label: 'Ignorado',
    payload: {
      classification_status: 'ignored',
      economic_type: null,
      cost_type: null,
      source_type_label: null,
      is_auto_mapped: false,
    },
  },
];

type ClassificationFormState = {
  classification_status: 'pending' | 'classified' | 'ignored';
  economic_type: EconomicType | '';
  cost_type: CostType | '';
  direct_cost_category: string;
  indirect_cost_category: string;
  notes: string;
  confidence: ConfidenceLevel | '';
  is_manual: boolean;
};

const initialClassificationForm: ClassificationFormState = {
  classification_status: 'classified',
  economic_type: '',
  cost_type: '',
  direct_cost_category: '',
  indirect_cost_category: '',
  notes: '',
  confidence: '',
  is_manual: true,
};

function buildClassificationForm(
  classification?: MovementClassification | null,
): ClassificationFormState {
  if (!classification) {
    return initialClassificationForm;
  }

  return {
    classification_status: classification.classification_status,
    economic_type: classification.economic_type ?? '',
    cost_type: classification.cost_type ?? '',
    direct_cost_category: classification.direct_cost_category ?? '',
    indirect_cost_category: classification.indirect_cost_category ?? '',
    notes: classification.notes ?? '',
    confidence: classification.confidence ?? '',
    is_manual: classification.is_manual,
  };
}

function resolveTypeMappingOptionKey(
  value: MovementImportTypeMappingValue | null | undefined,
): string {
  if (!value || !value.classification_status) {
    return 'unassigned';
  }

  if (value.classification_status === 'ignored') {
    return 'ignored';
  }

  if (value.classification_status === 'classified' && value.economic_type === 'expense') {
    return 'expense';
  }

  if (
    value.classification_status === 'classified' &&
    value.economic_type === 'cost' &&
    value.cost_type === 'direct'
  ) {
    return 'cost_direct';
  }

  if (
    value.classification_status === 'classified' &&
    value.economic_type === 'cost' &&
    value.cost_type === 'indirect'
  ) {
    return 'cost_indirect';
  }

  return 'unassigned';
}

function getTypeMappingPayloadFromOption(
  optionKey: string,
): MovementImportTypeMappingValue {
  return (
    TYPE_MAPPING_OPTIONS.find((option) => option.value === optionKey)?.payload ?? {
      classification_status: null,
      economic_type: null,
      cost_type: null,
      source_type_label: null,
      is_auto_mapped: false,
    }
  );
}

function getUploadHeaders(upload: Upload | null): Record<string, unknown> | null {
  if (!upload?.headers || typeof upload.headers !== 'object') {
    return null;
  }

  return upload.headers;
}

function getUploadCoverage(upload: Upload | null): Record<string, unknown> | null {
  const headers = getUploadHeaders(upload);
  const coverage = headers?.coverage_summary;

  if (!coverage || typeof coverage !== 'object') {
    return null;
  }

  return coverage as Record<string, unknown>;
}

export default function IndustrialCostMovementsView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<'all' | string>('all');
  const [monthFilter, setMonthFilter] = useState<'all' | string>('all');
  const [classificationFilter, setClassificationFilter] =
    useState<'all' | MovementClassificationBadgeStatus>('all');
  const [economicFilter, setEconomicFilter] = useState<
    | 'all'
    | 'expense'
    | 'cost_direct'
    | 'cost_indirect'
    | 'sales'
    | 'other_income'
    | 'non_imputable'
    | 'ignored'
    | 'pending'
  >('all');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState<MovementUploadMode>('replace');
  const [sourceGranularity, setSourceGranularity] = useState<SourceGranularity>('day');
  const [declaredPeriodYear, setDeclaredPeriodYear] = useState<string>('2025');
  const [declaredPeriodMonth, setDeclaredPeriodMonth] = useState<string>('');

  const [editingMovement, setEditingMovement] = useState<EconomicMovement | null>(null);
  const [deletingMovement, setDeletingMovement] = useState<EconomicMovement | null>(null);
  const [classificationForm, setClassificationForm] = useState<ClassificationFormState>(
    initialClassificationForm,
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<MovementImportPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [typeMapping, setTypeMapping] = useState<
    Record<string, MovementImportTypeMappingValue>
  >({});
  const [importSummary, setImportSummary] = useState<MovementImportSummary | null>(null);
  const [normalizationSheetName, setNormalizationSheetName] = useState<string | null>(null);
  const [normalizationHeaderRowIndex, setNormalizationHeaderRowIndex] = useState<number | null>(null);

  const [movementsQuery, classificationsQuery, uploadsQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-economic-movements'],
        queryFn: getIndustrialCostEconomicMovements,
      },
      {
        queryKey: ['industrial-cost-movement-classifications'],
        queryFn: getIndustrialCostMovementClassifications,
      },
      {
        queryKey: ['industrial-cost-uploads'],
        queryFn: getIndustrialCostUploads,
      },
    ],
  });
  const previewMutation = useMutation({
    mutationFn: ({
      file,
      sourceGranularity,
      declaredPeriodYear,
      declaredPeriodMonth,
      sheetName,
      headerRowIndex,
    }: {
      file: File;
      sourceGranularity: SourceGranularity;
      declaredPeriodYear: number | null;
      declaredPeriodMonth: number | null;
      sheetName: string | null;
      headerRowIndex: number | null;
    }) =>
      previewIndustrialCostEconomicMovementsImport(
        file,
        sourceGranularity,
        declaredPeriodYear,
        declaredPeriodMonth,
        sheetName,
        headerRowIndex,
      ),
    onSuccess: (result, variables) => {
      setSelectedFile(variables.file);
      setPreviewData(result);
      setMapping(result.auto_mapping);
      setTypeMapping(result.suggested_type_mapping);
      setImportSummary(null);
      setImportDialogOpen(true);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) {
        throw new Error('No hay archivo seleccionado');
      }

      return confirmIndustrialCostEconomicMovementsImport(
        selectedFile,
        mapping,
        typeMapping,
        uploadMode,
        sourceGranularity,
        declaredPeriodYear.trim() ? Number(declaredPeriodYear) : null,
        declaredPeriodMonth.trim() ? Number(declaredPeriodMonth) : null,
        normalizationSheetName,
        normalizationHeaderRowIndex,
      );
    },
    onSuccess: async (result) => {
      if (normalizationSheetName !== null || normalizationHeaderRowIndex !== null) {
        try {
          await commitFileNormalization({
            importer_key: 'economic_movements',
            sheet_name: normalizationSheetName,
            header_row_index: normalizationHeaderRowIndex ?? 0,
            data_start_row_index: (normalizationHeaderRowIndex ?? 0) + 1,
            column_mapping: Object.fromEntries(
              Object.entries(mapping).filter(([, v]) => v !== null) as [string, string][],
            ),
            known_columns: Object.values(mapping).filter((v): v is string => v !== null),
          });
        } catch {
          // El commit de normalización no es bloqueante
        }
      }

      setImportSummary(result);
      setPreviewData(null);
      setSelectedFile(null);
      setMapping({});
      setTypeMapping({});
      setNormalizationSheetName(null);
      setNormalizationHeaderRowIndex(null);
      setImportDialogOpen(false);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-economic-movements'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-movement-classifications'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-uploads'],
        }),
      ]);
    },
  });

  const upsertClassificationMutation = useMutation({
    mutationFn: (payload: UpsertIndustrialCostMovementClassificationPayload) =>
      upsertIndustrialCostMovementClassification(payload),
    onSuccess: async () => {
      enqueueSnackbar('Clasificación guardada correctamente.', { variant: 'success' });
      setEditingMovement(null);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-movement-classifications'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const movements = movementsQuery.data ?? [];
  const classifications = classificationsQuery.data ?? [];
  const uploads = uploadsQuery.data ?? [];

  const latestMovementUpload = useMemo(() => {
    return uploads.find((upload) => upload.upload_type === 'economic_movements') ?? null;
  }, [uploads]);

  const latestUploadHeaders = useMemo(() => {
    return getUploadHeaders(latestMovementUpload);
  }, [latestMovementUpload]);

  const latestUploadCoverage = useMemo(() => {
    return getUploadCoverage(latestMovementUpload);
  }, [latestMovementUpload]);

  const classificationsMap = useMemo(() => {
    return new Map<string, MovementClassification>(
      classifications.map((item) => [item.movement_id, item]),
    );
  }, [classifications]);

  const years = useMemo(() => {
    return [...new Set(movements.map((item) => item.period_year))]
      .sort((a, b) => b - a)
      .map(String);
  }, [movements]);

  const months = useMemo(() => {
    return [...new Set(movements.map((item) => item.period_month).filter((value) => value !== null))]
      .sort((a, b) => Number(a) - Number(b))
      .map(String);
  }, [movements]);

  const filteredMovements = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return movements.filter((movement: EconomicMovement) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        movement.description.toLowerCase().includes(normalizedSearch) ||
        movement.account_code.toLowerCase().includes(normalizedSearch) ||
        movement.source_type_code.toLowerCase().includes(normalizedSearch) ||
        (movement.supplier || '').toLowerCase().includes(normalizedSearch) ||
        (movement.document_ref || '').toLowerCase().includes(normalizedSearch);

      const matchesYear =
        yearFilter === 'all' || String(movement.period_year) === yearFilter;

      const matchesMonth =
        monthFilter === 'all' || String(movement.period_month) === monthFilter;

      const classificationStatus = resolveClassificationStatus(
        movement.id,
        classificationsMap,
      );

      const economicLabel = resolveEconomicClassificationLabel(
        movement,
        classificationsMap,
      );

      const matchesClassification =
        classificationFilter === 'all' ||
        classificationStatus === classificationFilter;

      const matchesEconomic =
        economicFilter === 'all' ||
        (economicFilter === 'expense' && economicLabel === 'Gasto') ||
        (economicFilter === 'cost_direct' && economicLabel === 'Coste directo') ||
        (economicFilter === 'cost_indirect' && economicLabel === 'Coste indirecto') ||
        (economicFilter === 'sales' && economicLabel === 'Venta') ||
        (economicFilter === 'other_income' && economicLabel === 'Otros ingresos') ||
        (economicFilter === 'non_imputable' && economicLabel === 'No imputable') ||
        (economicFilter === 'ignored' && economicLabel === 'Ignorado') ||
        (economicFilter === 'pending' && economicLabel === 'Pendiente');

      return (
        matchesSearch &&
        matchesYear &&
        matchesMonth &&
        matchesClassification &&
        matchesEconomic
      );
    });
  }, [
    movements,
    search,
    yearFilter,
    monthFilter,
    classificationFilter,
    economicFilter,
    classificationsMap,
  ]);

  const counts = useMemo(() => {
    const classified = movements.filter(
      (movement) =>
        resolveClassificationStatus(movement.id, classificationsMap) === 'classified',
    ).length;

    const ignored = movements.filter(
      (movement) =>
        resolveClassificationStatus(movement.id, classificationsMap) === 'ignored',
    ).length;

    return {
      total: movements.length,
      classified,
      ignored,
      unclassified: movements.length - classified - ignored,
    };
  }, [movements, classificationsMap]);

  const economicSummary = useMemo(() => {
    let expenseAmount = 0;
    let directAmount = 0;
    let indirectAmount = 0;
    let salesAmount = 0;
    let otherIncomeAmount = 0;
    let nonImputableAmount = 0;

    for (const movement of movements) {
      const economicLabel = resolveEconomicClassificationLabel(
        movement,
        classificationsMap,
      );

      if (economicLabel === 'Gasto') {
        expenseAmount += movement.amount;
      } else if (economicLabel === 'Coste directo') {
        directAmount += movement.amount;
      } else if (economicLabel === 'Coste indirecto') {
        indirectAmount += movement.amount;
      } else if (economicLabel === 'Venta') {
        salesAmount += movement.amount;
      } else if (economicLabel === 'Otros ingresos') {
        otherIncomeAmount += movement.amount;
      } else if (economicLabel === 'No imputable') {
        nonImputableAmount += movement.amount;
      }
    }

    return {
      expenseAmount,
      directAmount,
      indirectAmount,
      salesAmount,
      otherIncomeAmount,
      nonImputableAmount,
    };
  }, [movements, classificationsMap]);

  const typeCoverage = useMemo(() => {
    if (!previewData) {
      return {
        assignedRows: 0,
        unassignedRows: 0,
        assignedAmount: 0,
        unassignedAmount: 0,
      };
    }

    let assignedRows = 0;
    let unassignedRows = 0;
    let assignedAmount = 0;
    let unassignedAmount = 0;

    for (const item of previewData.detected_type_values) {
      const currentMapping =
        item.source_value !== '' ? typeMapping[item.source_value] : null;
      const isAssigned = Boolean(currentMapping?.classification_status);

      if (isAssigned) {
        assignedRows += item.movement_count;
        assignedAmount += item.amount_total;
      } else {
        unassignedRows += item.movement_count;
        unassignedAmount += item.amount_total;
      }
    }

    return {
      assignedRows,
      unassignedRows,
      assignedAmount,
      unassignedAmount,
    };
  }, [previewData, typeMapping]);

  const classificationCoveragePct = useMemo(() => {
    if (counts.total === 0) {
      return 0;
    }
    return (counts.classified / counts.total) * 100;
  }, [counts]);

  const isLoading =
    movementsQuery.isLoading || classificationsQuery.isLoading || uploadsQuery.isLoading;
  const isError =
    movementsQuery.isError || classificationsQuery.isError || uploadsQuery.isError;
  const error =
    movementsQuery.error ?? classificationsQuery.error ?? uploadsQuery.error;

  const missingRequiredFields = REQUIRED_FIELDS.filter((field) => !mapping[field]);
  const canConfirmImport =
    !!selectedFile &&
    missingRequiredFields.length === 0 &&
    !confirmMutation.isPending &&
    (sourceGranularity === 'day' ||
      (sourceGranularity === 'month' &&
        declaredPeriodYear.trim().length > 0 &&
        declaredPeriodMonth.trim().length > 0) ||
      (sourceGranularity === 'year' && declaredPeriodYear.trim().length > 0));

  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    if (previewMutation.isPending || confirmMutation.isPending) {
      return;
    }

    setImportDialogOpen(false);
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSelectedFile(null);
    setPreviewData(null);
    setMapping({});
    setTypeMapping({});
    setImportSummary(null);
    setNormalizationSheetName(null);
    setNormalizationHeaderRowIndex(null);

    try {
      const normalization = await analyzeFileNormalization(file, 'economic_movements');
      const resolvedSheetName = normalization.selected_sheet ?? null;
      const resolvedHeaderRowIndex = normalization.header_row_index ?? null;
      setNormalizationSheetName(resolvedSheetName);
      setNormalizationHeaderRowIndex(resolvedHeaderRowIndex);

      await previewMutation.mutateAsync({
        file,
        sourceGranularity,
        declaredPeriodYear: declaredPeriodYear.trim()
          ? Number(declaredPeriodYear)
          : null,
        declaredPeriodMonth: declaredPeriodMonth.trim()
          ? Number(declaredPeriodMonth)
          : null,
        sheetName: resolvedSheetName,
        headerRowIndex: resolvedHeaderRowIndex,
      });
    } finally {
      event.target.value = '';
    }
  };

  const handleMappingChange = (field: string, value: string) => {
    setMapping((prev) => ({
      ...prev,
      [field]: value === '' ? null : value,
    }));
  };

  const handleTypeMappingChange = (sourceValue: string, optionKey: string) => {
    setTypeMapping((prev) => ({
      ...prev,
      [sourceValue]: getTypeMappingPayloadFromOption(optionKey),
    }));
  };

  const handleOpenEdit = (movement: EconomicMovement) => {
    setEditingMovement(movement);
    setClassificationForm(buildClassificationForm(classificationsMap.get(movement.id)));
  };

  const handleClassificationFormChange = <K extends keyof ClassificationFormState>(
    key: K,
    value: ClassificationFormState[K],
  ) => {
    setClassificationForm((prev) => ({ ...prev, [key]: value }));
  };

  const canSaveClassification =
    classificationForm.classification_status === 'ignored' ||
    (classificationForm.classification_status === 'pending' &&
      !classificationForm.economic_type &&
      !classificationForm.cost_type) ||
    (!!classificationForm.economic_type &&
      (classificationForm.economic_type === 'expense' ||
        (!!classificationForm.cost_type &&
          (classificationForm.cost_type !== 'direct' ||
            classificationForm.direct_cost_category.trim().length > 0) &&
          (classificationForm.cost_type !== 'indirect' ||
            classificationForm.indirect_cost_category.trim().length > 0))));

  const handleSaveClassification = () => {
    if (!editingMovement) return;

    const payload: UpsertIndustrialCostMovementClassificationPayload = {
      movement_id: editingMovement.id,
      classification_status: classificationForm.classification_status,
      economic_type:
        classificationForm.classification_status === 'ignored' ||
        classificationForm.classification_status === 'pending'
          ? null
          : classificationForm.economic_type || null,
      cost_type:
        classificationForm.classification_status === 'ignored' ||
        classificationForm.classification_status === 'pending' ||
        classificationForm.economic_type !== 'cost'
          ? null
          : classificationForm.cost_type || null,
      direct_cost_category:
        classificationForm.classification_status === 'ignored' ||
        classificationForm.classification_status === 'pending' ||
        classificationForm.economic_type !== 'cost' ||
        classificationForm.cost_type !== 'direct'
          ? null
          : classificationForm.direct_cost_category.trim() || null,
      indirect_cost_category:
        classificationForm.classification_status === 'ignored' ||
        classificationForm.classification_status === 'pending' ||
        classificationForm.economic_type !== 'cost' ||
        classificationForm.cost_type !== 'indirect'
          ? null
          : classificationForm.indirect_cost_category.trim() || null,
      notes: classificationForm.notes.trim() || null,
      confidence:
        classificationForm.classification_status === 'ignored' ||
        classificationForm.classification_status === 'pending'
          ? null
          : classificationForm.confidence || null,
      is_manual: true,
    };

    upsertClassificationMutation.mutate(payload);
  };


  const handleConfirmDeleteMovement = () => {
    if (!deletingMovement) return;
    deleteMovementMutation.mutate(deletingMovement.id);
  };

  const deleteMovementMutation = useMutation({
    mutationFn: (movementId: string) => deleteIndustrialCostEconomicMovement(movementId),
    onSuccess: async () => {
      enqueueSnackbar('Movimiento eliminado correctamente.', { variant: 'success' });
      setDeletingMovement(null);
      if (editingMovement) {
        setEditingMovement(null);
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-economic-movements'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-movement-classifications'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-uploads'],
        }),
      ]);
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Movimientos
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Base económica operativa sobre la que se prepara la clasificación y el
              reparto de costes.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              Aquí debe quedar claro qué dataset está cargado, qué cobertura tiene y cómo
              están clasificados los movimientos antes de entrar a decisiones posteriores.
            </Typography>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.5,
          }}
        >
          {[
            { label: 'Total', value: counts.total },
            { label: 'Clasificados', value: counts.classified },
            { label: 'Pendientes', value: counts.unclassified },
            { label: 'Ignorados', value: counts.ignored },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.58)',
                px: 2,
                py: 1.75,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {item.label}
              </Typography>
              <Typography
                variant="h5"
                sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.4 }}
              >
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: 3,
            py: 3,
          }}
        >
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Estado actual del dataset
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  La carga debe sentirse como un estado persistente del sistema, no como un
                  evento efímero.
                </Typography>
              </Stack>

              <Button
                variant={latestMovementUpload ? 'outlined' : 'contained'}
                startIcon={<EditRoundedIcon />}
                onClick={handleOpenImportDialog}
                sx={{
                  borderRadius: 999,
                  px: 2.5,
                  py: 1.15,
                  fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: 'none',
                }}
              >
                {latestMovementUpload ? 'Actualizar archivo' : 'Cargar archivo'}
              </Button>
            </Stack>

            {latestMovementUpload ? (
              <Alert
                severity="success"
                variant="outlined"
                sx={{
                  bgcolor: 'rgba(34,197,94,0.08)',
                  borderColor: 'rgba(34,197,94,0.24)',
                  '& .MuiAlert-icon': { color: '#22c55e' },
                }}
              >
                Archivo activo <strong>{latestMovementUpload.original_filename}</strong>. Subido
                el <strong>{formatDateTime(latestMovementUpload.created_at)}</strong>.
              </Alert>
            ) : (
              <Alert severity="info">
                Todavía no hay una base económica cargada para este módulo.
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Archivo
                </Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                  {latestMovementUpload?.original_filename ?? '—'}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Granularidad / periodo
                </Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                  {formatGranularityLabel(
                    typeof latestUploadHeaders?.source_granularity === 'string'
                      ? latestUploadHeaders.source_granularity
                      : null,
                  )}{' '}
                  ·{' '}
                  {formatDeclaredPeriod(
                    typeof latestUploadHeaders?.declared_period_year === 'number'
                      ? latestUploadHeaders.declared_period_year
                      : null,
                    typeof latestUploadHeaders?.declared_period_month === 'number'
                      ? latestUploadHeaders.declared_period_month
                      : null,
                  )}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Rango cubierto
                </Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                  {typeof latestUploadHeaders?.source_granularity === 'string' &&
                  latestUploadHeaders.source_granularity !== 'day'
                    ? formatDeclaredPeriod(
                        typeof latestUploadHeaders?.declared_period_year === 'number'
                          ? latestUploadHeaders.declared_period_year
                          : null,
                        typeof latestUploadHeaders?.declared_period_month === 'number'
                          ? latestUploadHeaders.declared_period_month
                          : null,
                      )
                    : `${formatDate(
                        typeof latestUploadHeaders?.detected_date_min === 'string'
                          ? latestUploadHeaders.detected_date_min
                          : null,
                      )} — ${formatDate(
                        typeof latestUploadHeaders?.detected_date_max === 'string'
                          ? latestUploadHeaders.detected_date_max
                          : null,
                      )}`}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Cobertura clasificada
                </Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                  {formatPercent(classificationCoveragePct)}%
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.5)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Filas recibidas
                </Typography>
                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                  {latestMovementUpload?.approx_rows ?? '—'}
                </Typography>
              </Paper>
            </Box>

            {latestUploadCoverage ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Fechas válidas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                    {String(latestUploadCoverage.rows_with_valid_date ?? '—')}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Fechas inválidas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                    {String(latestUploadCoverage.rows_with_invalid_date ?? '—')}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Movimientos con tipo
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                    {String(latestUploadCoverage.rows_with_type_value ?? '—')}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Movimientos sin tipo
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                    {String(latestUploadCoverage.rows_without_type_value ?? '—')}
                  </Typography>
                </Paper>
              </Box>
            ) : null}

            <Dialog
              open={importDialogOpen}
              onClose={handleCloseImportDialog}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>Actualizar movimientos</DialogTitle>

              <DialogContent>
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.5,
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.16)',
                    bgcolor: 'rgba(15,23,42,0.52)',
                    px: 2,
                    py: 2,
                  }}
                >
                <Stack spacing={2.25}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.75}>
                      <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Actualización de movimientos
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.65 }}>
                        El sistema intentará reconocer columnas y tipos. Si no asigna todo,
                        el usuario puede completar el criterio sin bloquear la importación.
                      </Typography>
                    </Stack>

                    <Button
                      variant="contained"
                      startIcon={
                        previewMutation.isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <UploadFileRoundedIcon />
                        )
                      }
                      onClick={handleOpenFileDialog}
                      disabled={previewMutation.isPending || confirmMutation.isPending}
                      sx={{
                        borderRadius: 999,
                        px: 2.5,
                        py: 1.15,
                        fontWeight: 800,
                        textTransform: 'none',
                        boxShadow: 'none',
                      }}
                    >
                      {previewMutation.isPending ? 'Leyendo archivo...' : 'Subir archivo'}
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      select
                      label="Granularidad del archivo"
                      value={sourceGranularity}
                      onChange={(e) => {
                        const next = e.target.value as SourceGranularity;
                        setSourceGranularity(next);

                        if (next === 'day') {
                          setUploadMode('replace_by_date_range');
                          setDeclaredPeriodMonth('');
                        } else if (next === 'month') {
                          setUploadMode('replace_by_period_range');
                        } else {
                          setUploadMode('replace_by_period_range');
                          setDeclaredPeriodMonth('');
                        }
                      }}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="day">Diaria (con fecha real por fila)</MenuItem>
                      <MenuItem value="month">Mensual agregada</MenuItem>
                      <MenuItem value="year">Anual agregada</MenuItem>
                    </TextField>

                    <TextField
                      label="Año declarado"
                      type="number"
                      value={declaredPeriodYear}
                      onChange={(e) => setDeclaredPeriodYear(e.target.value)}
                      fullWidth
                      size="small"
                      disabled={sourceGranularity === 'day'}
                    />

                    <TextField
                      label="Mes declarado"
                      type="number"
                      value={declaredPeriodMonth}
                      onChange={(e) => setDeclaredPeriodMonth(e.target.value)}
                      fullWidth
                      size="small"
                      inputProps={{ min: 1, max: 12 }}
                      disabled={sourceGranularity !== 'month'}
                    />
                    <TextField
                      select
                      label="Modo de carga"
                      value={uploadMode}
                      onChange={(e) =>
                        setUploadMode(e.target.value as MovementUploadMode)
                      }
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="replace">Reemplazo total</MenuItem>
                      <MenuItem value="incremental">Incremental</MenuItem>
                      {sourceGranularity === 'day' ? (
                        <MenuItem value="replace_by_date_range">
                          Reemplazo por rango de fechas
                        </MenuItem>
                      ) : (
                        <MenuItem value="replace_by_period_range">
                          Reemplazo por periodo declarado
                        </MenuItem>
                      )}
                    </TextField>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.14)',
                        bgcolor: 'rgba(15,23,42,0.5)',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Efecto esperado
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}
                      >
                        {uploadMode === 'replace'
                          ? 'Sustituye toda la base activa'
                          : uploadMode === 'incremental'
                            ? 'Añade movimientos sin borrar los existentes'
                            : uploadMode === 'replace_by_date_range'
                              ? 'Sustituye solo el rango real de fechas detectado'
                              : 'Sustituye solo el periodo declarado del archivo'}
                      </Typography>
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.14)',
                        bgcolor: 'rgba(15,23,42,0.5)',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Archivo actual
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}
                      >
                        {latestMovementUpload?.original_filename ?? 'Sin base previa'}
                      </Typography>
                    </Paper>
                  </Box>

                  <Alert severity="info">
                    El modo de carga define cómo interactúa este archivo con la base económica
                    ya existente. No debe quedar implícito.
                  </Alert>

                  <Alert severity="info" variant="outlined">
                    {sourceGranularity === 'day'
                      ? 'En granularidad diaria, FECHA debe existir y el sistema trabajará con rango real detectado.'
                      : sourceGranularity === 'month'
                        ? 'En granularidad mensual, FECHA deja de ser obligatoria y el periodo se declara manualmente.'
                        : 'En granularidad anual, FECHA deja de ser obligatoria y el archivo se interpreta como agregado anual.'}
                  </Alert>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    hidden
                    onChange={handleFileSelected}
                  />

                  {previewMutation.isError ? (
                    <Alert severity="error">{getErrorMessage(previewMutation.error)}</Alert>
                  ) : null}

                  {previewData ? (
                    <Stack spacing={2.25}>
                      <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Archivo detectado: {previewData.filename}
                      </Typography>

                      <Alert severity="info" variant="outlined">
                        Granularidad detectada para esta carga:{' '}
                        <strong>{formatGranularityLabel(previewData.source_granularity)}</strong>
                        {previewData.source_granularity !== 'day'
                          ? ` · Periodo declarado: ${formatDeclaredPeriod(
                              previewData.declared_period_year,
                              previewData.declared_period_month,
                            )}`
                          : ''}
                      </Alert>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                          gap: 1.5,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Fecha mínima
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {previewData.source_granularity === 'day'
                              ? formatDate(previewData.detected_date_min)
                              : formatDeclaredPeriod(
                                  previewData.declared_period_year,
                                  previewData.declared_period_month,
                                )}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Fecha máxima
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {previewData.source_granularity === 'day'
                              ? formatDate(previewData.detected_date_max)
                              : formatDeclaredPeriod(
                                  previewData.declared_period_year,
                                  previewData.declared_period_month,
                                )}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Filas con tipo asignado
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {typeCoverage.assignedRows} / {previewData.coverage_summary.total_rows}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Importe pendiente en tipo
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {formatAmount(typeCoverage.unassignedAmount)}
                          </Typography>
                        </Paper>
                      </Box>

                      <Stack spacing={1}>
                        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                          1. Mapeo de columnas
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5,
                          }}
                        >
                          {(sourceGranularity === 'day'
                            ? ['FECHA', ...REQUIRED_FIELDS]
                            : REQUIRED_FIELDS
                          ).map((field) => (
                            <TextField
                              key={field}
                              select
                              label={field}
                              value={mapping[field] ?? ''}
                              onChange={(e) => handleMappingChange(field, e.target.value)}
                              fullWidth
                              size="small"
                            >
                              <MenuItem value="">Sin asignar</MenuItem>
                              {previewData.columns.map((column) => (
                                <MenuItem key={`${field}-${column}`} value={column}>
                                  {column}
                                </MenuItem>
                              ))}
                            </TextField>
                          ))}
                        </Box>

                        {missingRequiredFields.length > 0 ? (
                          <Alert severity="warning">
                            Faltan campos obligatorios por asignar: {missingRequiredFields.join(', ')}
                          </Alert>
                        ) : (
                          <Alert severity="info">
                            Estructura reconocida. Ya puedes revisar el significado económico de los códigos.
                          </Alert>
                        )}
                      </Stack>

                      <Stack spacing={1}>
                        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                          2. Mapeo de valores del campo tipo
                        </Typography>

                        {previewData.detected_type_values.length === 0 ? (
                          <Alert severity="info">
                            No se han detectado valores de tipo con la asignación actual.
                          </Alert>
                        ) : (
                          <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>
                                    Valor origen
                                  </TableCell>
                                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                                    Movimientos
                                  </TableCell>
                                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                                    Importe
                                  </TableCell>
                                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>
                                    Asignación app
                                  </TableCell>
                                  <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>
                                    Estado
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {previewData.detected_type_values.map((item) => {
                                  const currentMapping =
                                    item.source_value !== ''
                                      ? typeMapping[item.source_value]
                                      : null;

                                  const optionKey = resolveTypeMappingOptionKey(currentMapping);

                                  return (
                                    <TableRow key={item.is_empty ? '__EMPTY__' : item.source_value}>
                                      <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                        {item.is_empty ? '(vacío)' : item.source_value}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                                        {item.movement_count}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#cbd5e1' }}>
                                        {formatAmount(item.amount_total)}
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 240 }}>
                                        {item.is_empty ? (
                                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                            Sin valor de origen
                                          </Typography>
                                        ) : (
                                          <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={optionKey}
                                            onChange={(e) =>
                                              handleTypeMappingChange(
                                                item.source_value,
                                                e.target.value,
                                              )
                                            }
                                          >
                                            {TYPE_MAPPING_OPTIONS.map((option) => (
                                              <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                              </MenuItem>
                                            ))}
                                          </TextField>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ color: '#cbd5e1' }}>
                                        {item.is_empty ? (
                                          <Chip
                                            label="Pendiente"
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(148,163,184,0.10)',
                                              color: '#cbd5e1',
                                              border: '1px solid rgba(148,163,184,0.22)',
                                              fontWeight: 800,
                                            }}
                                          />
                                        ) : currentMapping?.classification_status ? (
                                          <Chip
                                            label={
                                              currentMapping.is_auto_mapped
                                                ? 'Automático'
                                                : 'Asignado'
                                            }
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(34,197,94,0.10)',
                                              color: '#22c55e',
                                              border: '1px solid rgba(34,197,94,0.24)',
                                              fontWeight: 800,
                                            }}
                                          />
                                        ) : (
                                          <Chip
                                            label="Pendiente"
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(245,158,11,0.10)',
                                              color: '#f59e0b',
                                              border: '1px solid rgba(245,158,11,0.24)',
                                              fontWeight: 800,
                                            }}
                                          />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.5,
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Cobertura de asignación
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {previewData.coverage_summary.total_rows > 0
                                ? `${formatPercent(
                                    (typeCoverage.assignedRows /
                                      previewData.coverage_summary.total_rows) *
                                      100,
                                  )}%`
                                : '0%'}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Filas pendientes
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {typeCoverage.unassignedRows}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Importe asignado
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {formatAmount(typeCoverage.assignedAmount)}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Importe sin asignar
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {formatAmount(typeCoverage.unassignedAmount)}
                            </Typography>
                          </Paper>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={1.5}>
                        <Button
                          variant="contained"
                          onClick={() => confirmMutation.mutate()}
                          disabled={!canConfirmImport}
                          sx={{
                            borderRadius: 999,
                            px: 2.5,
                            py: 1.15,
                            fontWeight: 800,
                            textTransform: 'none',
                            boxShadow: 'none',
                          }}
                        >
                          {confirmMutation.isPending ? 'Importando...' : 'Confirmar importación'}
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={() => {
                            setSelectedFile(null);
                            setPreviewData(null);
                            setMapping({});
                            setTypeMapping({});
                          }}
                          disabled={confirmMutation.isPending}
                          sx={{
                            borderRadius: 999,
                            px: 2.5,
                            py: 1.15,
                            fontWeight: 800,
                            textTransform: 'none',
                          }}
                        >
                          Cancelar
                        </Button>
                      </Stack>

                      <Alert severity="info">
                        Si quedan tipos sin asignar, los movimientos se importarán igualmente y quedarán pendientes de revisión posterior.
                      </Alert>

                      {confirmMutation.isError ? (
                        <Alert severity="error">{getErrorMessage(confirmMutation.error)}</Alert>
                      ) : null}
                    </Stack>
                  ) : null}

                  {importSummary ? (
                    <Box>
                      <Alert severity="success">
                        Archivo <strong>{importSummary.filename}</strong> procesado. Importadas{' '}
                        <strong>{importSummary.rows_imported}</strong> filas válidas de{' '}
                        <strong>{importSummary.rows_received}</strong>. Clasificaciones automáticas:{' '}
                        <strong>{importSummary.classifications_created}</strong>. Pendientes de clasificación:{' '}
                        <strong>{importSummary.rows_pending_classification}</strong>. Modo aplicado:{' '}
                        <strong>
                          {importSummary.impact_summary?.upload_mode === 'replace'
                            ? 'reemplazo total'
                            : importSummary.impact_summary?.upload_mode === 'incremental'
                              ? 'incremental'
                              : importSummary.impact_summary?.upload_mode === 'replace_by_date_range'
                                ? 'reemplazo por rango de fechas'
                                : importSummary.impact_summary?.upload_mode === 'replace_by_period_range'
                                  ? 'reemplazo por periodo'
                                  : 'n/d'}
                        </strong>.
                      </Alert>

                      <Box
                        sx={{
                          mt: 2,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                          gap: 1.5,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Fecha mínima
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {importSummary.source_granularity === 'day'
                              ? formatDate(importSummary.detected_date_min)
                              : formatDeclaredPeriod(
                                  importSummary.declared_period_year,
                                  importSummary.declared_period_month,
                                )}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Fecha máxima
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {importSummary.source_granularity === 'day'
                              ? formatDate(importSummary.detected_date_max)
                              : formatDeclaredPeriod(
                                  importSummary.declared_period_year,
                                  importSummary.declared_period_month,
                                )}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Filas válidas
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {importSummary.rows_valid}
                          </Typography>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(148,163,184,0.14)',
                            bgcolor: 'rgba(15,23,42,0.5)',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Pendientes de clasificación
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                            {importSummary.rows_pending_classification}
                          </Typography>
                        </Paper>
                      </Box>

                      {importSummary.impact_summary ? (
                        <Box
                          sx={{
                            mt: 2,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
                            gap: 1.5,
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Modo aplicado
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {importSummary.impact_summary.upload_mode === 'replace'
                                ? 'Reemplazo total'
                                : importSummary.impact_summary.upload_mode === 'incremental'
                                  ? 'Incremental'
                                  : 'Reemplazo por rango'}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Existían antes
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {importSummary.impact_summary.existing_movements_before}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Movimientos borrados
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {importSummary.impact_summary.deleted_movements}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Clasificaciones borradas
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {importSummary.impact_summary.deleted_classifications}
                            </Typography>
                          </Paper>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(148,163,184,0.14)',
                              bgcolor: 'rgba(15,23,42,0.5)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                              Quedan después
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, mt: 0.5 }}>
                              {importSummary.impact_summary.existing_movements_after}
                            </Typography>
                          </Paper>
                        </Box>
                      ) : null}
                    </Box>
                  ) : null}
                </Stack>
                </Paper>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button
                  onClick={handleCloseImportDialog}
                  disabled={previewMutation.isPending || confirmMutation.isPending}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {[
            { label: 'Coste directo', value: economicSummary.directAmount },
            { label: 'Coste indirecto', value: economicSummary.indirectAmount },
            { label: 'Gastos', value: economicSummary.expenseAmount },
            { label: 'Ventas', value: economicSummary.salesAmount },
            { label: 'Otros ingresos', value: economicSummary.otherIncomeAmount },
            { label: 'No imputable', value: economicSummary.nonImputableAmount },
          ].map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.58)',
                px: 2,
                py: 1.75,
              }}
            >
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.4 }}
              >
                {formatAmount(item.value)}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: 3,
            py: 3,
          }}
        >
          <Stack spacing={2.25}>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
              Filtros operativos
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 1fr' },
                gap: 1.5,
              }}
            >
              <TextField
                label="Buscar"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                size="small"
                placeholder="Cuenta, descripción, tipo, proveedor o referencia"
              />

              <TextField
                select
                label="Año"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="all">Todos</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Mes"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="all">Todos</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month.padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Estado"
                value={classificationFilter}
                onChange={(e) =>
                  setClassificationFilter(
                    e.target.value as 'all' | MovementClassificationBadgeStatus,
                  )
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="unclassified">Pendiente</MenuItem>
                <MenuItem value="classified">Clasificado</MenuItem>
                <MenuItem value="ignored">Ignorado</MenuItem>
              </TextField>

              <TextField
                select
                label="Clasificación económica"
                value={economicFilter}
                onChange={(e) =>
                  setEconomicFilter(
                    e.target.value as
                      | 'all'
                      | 'expense'
                      | 'cost_direct'
                      | 'cost_indirect'
                      | 'ignored'
                      | 'pending',
                  )
                }
                fullWidth
                size="small"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="expense">Gasto</MenuItem>
                <MenuItem value="cost_direct">Coste directo</MenuItem>
                <MenuItem value="cost_indirect">Coste indirecto</MenuItem>
                <MenuItem value="sales">Ventas</MenuItem>
                <MenuItem value="other_income">Otros ingresos</MenuItem>
                <MenuItem value="non_imputable">No imputable</MenuItem>
                <MenuItem value="ignored">Ignorado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
              </TextField>
            </Box>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
              Base económica del periodo
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Aquí debe quedar visible la clasificación económica y debe poder corregirse cada movimiento.
            </Typography>
          </Box>

          {isError ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{getErrorMessage(error)}</Alert>
            </Box>
          ) : isLoading ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Cargando movimientos...
              </Typography>
            </Box>
          ) : movements.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin movimientos todavía"
                description="Carga un archivo de movimientos para disponer de base real sobre la que clasificar y construir el coste."
                note="La vista está preparada para mostrar el estado persistente del dataset y abrir la actualización solo cuando haga falta."
              />
            </Box>
          ) : filteredMovements.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <IndustrialCostSectionPlaceholder
                title="Sin resultados para los filtros actuales"
                description="Ajusta los filtros para volver a mostrar movimientos del periodo."
              />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Fecha</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Cuenta</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Tipo</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Proveedor</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Referencia</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800, minWidth: 280 }}>
                      Descripción
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                      Importe
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Periodo</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>
                      Clasificación económica
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredMovements.map((movement) => {
                    const economicLabel = resolveEconomicClassificationLabel(
                      movement,
                      classificationsMap,
                    );
                    const economicCfg =
                      ECONOMIC_CLASSIFICATION_CONFIG[economicLabel] ??
                      ECONOMIC_CLASSIFICATION_CONFIG.Pendiente;

                    return (
                      <TableRow key={movement.id} hover>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {formatDate(movement.movement_date)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1', fontFamily: 'monospace' }}>
                          {movement.account_code || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {movement.source_type_code || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {movement.supplier || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {movement.document_ref || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          {movement.description}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                          {formatAmount(movement.amount)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          {formatPeriod(movement.period_year, movement.period_month)}
                        </TableCell>
                        <TableCell sx={{ color: '#cbd5e1' }}>
                          <Chip
                            label={economicLabel}
                            size="small"
                            sx={{
                              bgcolor: economicCfg.bg,
                              color: economicCfg.color,
                              border: `1px solid ${economicCfg.border}`,
                              fontWeight: 800,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenEdit(movement)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderColor: 'rgba(56,189,248,0.28)',
                              color: '#38bdf8',
                            }}
                          >
                            Revisar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={!!editingMovement}
        onClose={() => !upsertClassificationMutation.isPending && setEditingMovement(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Clasificar movimiento</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingMovement ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.04)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {editingMovement.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {editingMovement.account_code || 'Sin cuenta'} ·{' '}
                    {editingMovement.source_type_code || 'Sin tipo'} ·{' '}
                    {editingMovement.supplier || 'Sin proveedor'} ·{' '}
                    {formatAmount(editingMovement.amount)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            <TextField
              select
              label="Estado de clasificación"
              value={classificationForm.classification_status}
              onChange={(e) =>
                handleClassificationFormChange(
                  'classification_status',
                  e.target.value as ClassificationFormState['classification_status'],
                )
              }
              fullWidth
              size="small"
            >
              <MenuItem value="classified">Clasificado</MenuItem>
              <MenuItem value="ignored">Ignorado</MenuItem>
              <MenuItem value="pending">Pendiente</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo económico"
              value={classificationForm.economic_type}
              onChange={(e) =>
                handleClassificationFormChange(
                  'economic_type',
                  e.target.value as EconomicType | '',
                )
              }
              fullWidth
              size="small"
              disabled={
                classificationForm.classification_status === 'ignored' ||
                classificationForm.classification_status === 'pending'
              }
            >
              <MenuItem value="">Seleccionar</MenuItem>
              <MenuItem value="expense">Gasto</MenuItem>
              <MenuItem value="cost">Coste</MenuItem>
            </TextField>

            <TextField
              select
              label="Tipo de coste"
              value={classificationForm.cost_type}
              onChange={(e) =>
                handleClassificationFormChange('cost_type', e.target.value as CostType | '')
              }
              fullWidth
              size="small"
              disabled={
                classificationForm.classification_status === 'ignored' ||
                classificationForm.classification_status === 'pending' ||
                classificationForm.economic_type !== 'cost'
              }
            >
              <MenuItem value="">Seleccionar</MenuItem>
              <MenuItem value="direct">Directo</MenuItem>
              <MenuItem value="indirect">Indirecto</MenuItem>
            </TextField>

            {classificationForm.economic_type === 'cost' &&
            classificationForm.cost_type === 'direct' &&
            classificationForm.classification_status === 'classified' ? (
              <TextField
                label="Categoría de coste directo"
                value={classificationForm.direct_cost_category}
                onChange={(e) =>
                  handleClassificationFormChange('direct_cost_category', e.target.value)
                }
                fullWidth
                size="small"
              />
            ) : null}

            {classificationForm.economic_type === 'cost' &&
            classificationForm.cost_type === 'indirect' &&
            classificationForm.classification_status === 'classified' ? (
              <TextField
                label="Categoría de coste indirecto"
                value={classificationForm.indirect_cost_category}
                onChange={(e) =>
                  handleClassificationFormChange('indirect_cost_category', e.target.value)
                }
                fullWidth
                size="small"
              />
            ) : null}

            <TextField
              select
              label="Confianza"
              value={classificationForm.confidence}
              onChange={(e) =>
                handleClassificationFormChange(
                  'confidence',
                  e.target.value as ConfidenceLevel | '',
                )
              }
              fullWidth
              size="small"
              disabled={
                classificationForm.classification_status === 'ignored' ||
                classificationForm.classification_status === 'pending'
              }
            >
              <MenuItem value="">Sin informar</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Media</MenuItem>
              <MenuItem value="low">Baja</MenuItem>
            </TextField>

            <TextField
              label="Notas"
              value={classificationForm.notes}
              onChange={(e) => handleClassificationFormChange('notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            color="error"
            onClick={() => {
              if (editingMovement) {
                setDeletingMovement(editingMovement);
              }
            }}
            disabled={upsertClassificationMutation.isPending || deleteMovementMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700, mr: 'auto' }}
          >
            Borrar registro
          </Button>
          <Button
            onClick={() => setEditingMovement(null)}
            disabled={upsertClassificationMutation.isPending || deleteMovementMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveClassification}
            disabled={!canSaveClassification || upsertClassificationMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {upsertClassificationMutation.isPending ? 'Guardando...' : 'Guardar clasificación'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!deletingMovement}
        onClose={() => !deleteMovementMutation.isPending && setDeletingMovement(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar movimiento</DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Esta acción eliminará el movimiento seleccionado y su clasificación asociada.
            </Typography>

            {deletingMovement ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.04)',
                  px: 2,
                  py: 1.5,
                }}
              >
                <Stack spacing={0.75}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {deletingMovement.description}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {deletingMovement.account_code || 'Sin cuenta'} ·{' '}
                    {formatAmount(deletingMovement.amount)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeletingMovement(null)}
            disabled={deleteMovementMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteMovement}
            disabled={deleteMovementMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {deleteMovementMutation.isPending ? 'Borrando...' : 'Confirmar borrado'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}